import { useTranslation } from "react-i18next";
import {
  getLocalizedText,
  type NotiAction,
  type RemoteNoti,
} from "../../api/notifications";
import { parseInvitationAction } from "../../api/followerInvitations";

interface Props {
  noti: RemoteNoti;
  onClick: (noti: RemoteNoti) => void;
  onAction?(noti: RemoteNoti, action: NotiAction): void;
  busy?: boolean;
  doneLabel?: string | null;
}

export default function NotificationItem({
  noti,
  onClick,
  onAction,
  busy = false,
  doneLabel = null,
}: Props) {
  const { i18n } = useTranslation();
  const isRead = noti.status === "READ";
  const message = getLocalizedText(noti.message, i18n.language);
  const time = noti.createdAt
    ? new Date(noti.createdAt).toLocaleDateString()
    : "";
  const actions = (noti.actions ?? []).filter(
    (a) => parseInvitationAction(a) !== null,
  );

  return (
    <div
      data-testid="notification-item"
      onClick={() => onClick(noti)}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        padding: "12px 16px",
        cursor: "pointer",
        background: isRead ? "transparent" : "var(--bg-elevated)",
        borderBottom: "1px solid var(--border)",
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: noti.icon?.rounded === false ? 8 : "50%",
          flexShrink: 0,
          background: "var(--bg-surface)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        {noti.icon?.url ? (
          <img
            src={noti.icon.url}
            alt=""
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <span style={{ fontSize: 18 }}>🔔</span>
        )}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <p
          style={{
            margin: 0,
            fontSize: 14,
            color: "var(--foreground)",
            lineHeight: 1.4,
          }}
        >
          {message}
        </p>
        {time && (
          <p
            style={{
              margin: "4px 0 0",
              fontSize: 12,
              color: "var(--foreground-subtle)",
            }}
          >
            {time}
          </p>
        )}
        {doneLabel ? (
          <p
            data-testid="action-done"
            style={{
              margin: "8px 0 0",
              fontSize: 12,
              color: "var(--foreground-subtle)",
            }}
          >
            {doneLabel}
          </p>
        ) : (
          actions.length > 0 && (
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              {actions.map((action, i) => (
                <button
                  type="button"
                  key={action.code ?? i}
                  disabled={busy}
                  onClick={(e) => {
                    e.stopPropagation();
                    onAction?.(noti, action);
                  }}
                  style={{
                    fontSize: 12,
                    color: "var(--primary-light)",
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: busy ? "default" : "pointer",
                    opacity: busy ? 0.5 : 1,
                  }}
                >
                  {action.name}
                </button>
              ))}
            </div>
          )
        )}
      </div>

      {!isRead && (
        <div
          data-testid="unread-dot"
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: "var(--primary)",
            flexShrink: 0,
            marginTop: 4,
          }}
        />
      )}
    </div>
  );
}
